import Footer from '@components/Footer';
import { FooterActionsProvider, useFooterActions } from '@components/Footer/FooterActionsContext';
import Header from '@components/Header';
import { HeaderActionsProvider } from '@components/Header/HeaderActionsContext';
import Container from '@ds/components/layout/Container';
import Grid from '@ds/components/layout/Grid';
import { isDevelopment } from '@utils/is-development';
import clsx from 'clsx';
import React, { type ReactNode, useEffect, useState } from 'react';

import styles from './PageLayout.module.css';

interface PageLayoutProps {
    children?: ReactNode;
    className?: string;
    /**
     * Page-specific navigation passed to the footer's "with nav" band
     * (e.g. Previous / Next / Submit). When omitted, the plain copyright
     * footer renders. See `Footer`'s `actions` prop.
     */
    footerActions?: ReactNode;
    /**
     * Suppresses the footer entirely — for full-height pages (e.g. a
     * device/map view) where a footer would intrude.
     * @default false
     */
    hideFooter?: boolean;
}

const PageLayout = ({ children, className, footerActions, hideFooter = false }: PageLayoutProps) => {
    const [showGrid, setShowGrid] = useState(() => isDevelopment() && localStorage.getItem('debug:grid') === 'true');

    useEffect(() => {
        if (!isDevelopment()) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.shiftKey && e.key === 'G') {
                setShowGrid((prev) => {
                    const next = !prev;
                    localStorage.setItem('debug:grid', String(next));
                    return next;
                });
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    return (
        <HeaderActionsProvider>
            <FooterActionsProvider>
                <PageLayoutShell
                    className={className}
                    footerActions={footerActions}
                    hideFooter={hideFooter}
                    showGrid={showGrid}
                >
                    {children}
                </PageLayoutShell>
            </FooterActionsProvider>
        </HeaderActionsProvider>
    );
};

interface PageLayoutShellProps
    extends Pick<PageLayoutProps, 'children' | 'className' | 'footerActions' | 'hideFooter'> {
    showGrid: boolean;
}

/**
 * Inner shell rendered beneath the providers so it can consume footer actions
 * pushed up via `useFooterActions`. Context actions take precedence over the
 * static `footerActions` prop, so a page can drive the nav band imperatively.
 */
const PageLayoutShell = ({ children, className, footerActions, hideFooter, showGrid }: PageLayoutShellProps) => {
    const { actions: contextFooterActions } = useFooterActions();
    const footerActionsToRender = contextFooterActions ?? footerActions;

    return (
        <>
            <div className={styles.shell}>
                <Header />
                <Container
                    as="main"
                    className={clsx(styles.main, className)}
                >
                    {children}
                </Container>
                {!hideFooter && <Footer actions={footerActionsToRender} />}
            </div>
            {isDevelopment() && showGrid && <Grid.Debug />}
        </>
    );
};

PageLayoutShell.displayName = 'PageLayoutShell';

PageLayout.displayName = 'PageLayout';

export default PageLayout;
